import React from 'react';
import useUserStore from '../store/userStore';
import { calcPersonalYear } from '../utils/numbers';
import { ROOT_DATA } from '../utils/data';
import { C } from '../theme';
import { Card, CardTitle, RCard, QuoteBox } from '../components/Card';

export default function ProfileScreen({ onLogout }) {
  const { user, subscription } = useUserStore();
  const root = user?.root_number;
  const rd = ROOT_DATA[root];
  const pyear = user?.birth_day && user?.birth_month
    ? calcPersonalYear(user.birth_day, user.birth_month) : user?.personal_year ?? null;
  const pyrd = ROOT_DATA[pyear] || ROOT_DATA[1];

  if (!rd) return (
    <div style={{ textAlign:'center', padding:'40px 20px', color:C.text2, fontFamily:'Cairo,sans-serif' }}>
      <div style={{ fontFamily:'Amiri,serif', fontSize:20, color:C.gold, marginBottom:8 }}>لا يوجد بيانات</div>
      <div>أكمل الـ Onboarding لعرض ملفك</div>
    </div>
  );

  return (
    <div>
      {/* Root hero */}
      <div style={{ background:`linear-gradient(135deg,${C.bg3},${C.bg4})`, border:`1px solid ${C.goldDim}`, borderRadius:18, padding:24, marginBottom:14, textAlign:'center' }}>
        <div style={{ fontFamily:'Amiri,serif', fontSize:72, color:C.gold, lineHeight:1, textShadow:'0 0 40px rgba(212,168,71,.45)', marginBottom:8 }}>{root}</div>
        <div style={{ fontSize:15, color:C.text, marginBottom:3, fontWeight:500 }}>{rd.title}</div>
        <div style={{ fontSize:12, color:C.text2, lineHeight:1.8 }}>{rd.planet} · {rd.hz} Hz · الرقم المضاد: {rd.contra}</div>
        <div style={{ display:'flex', justifyContent:'center', gap:20, marginTop:14, flexWrap:'wrap' }}>
          {[
            { lbl:'الكوكب', val:rd.planet, extra:'' },
            { lbl:'التردد الصوتي', val:`${rd.hz} Hz`, extra:'' },
            { lbl:'الرقم المضاد', val:rd.contra, blue:true },
          ].map(item=>(
            <div key={item.lbl} style={{ textAlign:'center', background:item.blue?'rgba(91,127,212,.1)':'rgba(212,168,71,.08)', border:`1px solid ${item.blue?C.accent:C.goldDim}`, borderRadius:10, padding:'10px 16px' }}>
              <span style={{ fontSize:10, color:C.text2, display:'block', marginBottom:3, letterSpacing:.5 }}>{item.lbl}</span>
              <span style={{ fontSize:15, color:item.blue?'#8ab0d8':C.gold, fontWeight:600 }}>{item.val}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Traits */}
      <Card>
        <CardTitle>صفاتك الجوهرية</CardTitle>
        <RCard>
          <div style={{ fontSize:14, lineHeight:1.75, marginBottom:8 }}>{rd.desc}</div>
          <div style={{ fontSize:12, color:C.text2, lineHeight:1.6, borderTop:`1px solid ${C.border}`, paddingTop:10, marginTop:10 }}>
            <ul style={{ listStyle:'none', padding:0, margin:0 }}>
              {rd.traits.map(t=><li key={t} style={{ marginBottom:5, paddingRight:14, position:'relative', lineHeight:1.6 }}><span style={{ position:'absolute', right:0, color:C.goldDim, fontSize:7, top:4 }}>◆</span>{t}</li>)}
            </ul>
          </div>
        </RCard>
        <RCard type="warn">
          <div style={{ fontSize:12, color:C.red, fontWeight:600, marginBottom:8 }}>⚠ التحذير الخفي</div>
          <div style={{ fontSize:14, lineHeight:1.75 }}>{rd.warn}</div>
        </RCard>
      </Card>

      {/* Contra */}
      <Card>
        <CardTitle>رقمك المضاد — مفتاح التحرر</CardTitle>
        <div style={{ background:C.bg2, border:`1px solid ${C.accent}`, borderRadius:14, padding:16, display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ width:52, height:52, background:`linear-gradient(135deg,${C.accent},${C.purple})`, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:700, color:'white', flexShrink:0, fontFamily:'Amiri,serif' }}>{rd.contra}</div>
          <div>
            <div style={{ fontSize:13, color:C.gold, marginBottom:4 }}>{ROOT_DATA[rd.contra]?.title||''} — طاقة التحرر</div>
            <div style={{ fontSize:12, color:C.text2, lineHeight:1.5 }}>اكتب اسمك 7 مرات تحت الرقم {rd.contra}، لمدة 3 أيام متتالية قبل شروق الشمس.</div>
          </div>
        </div>
      </Card>

      {/* Personal year 2026 */}
      {pyear && (
        <Card>
          <CardTitle>سنتك الشخصية 2026</CardTitle>
          <RCard type="pos">
            <div style={{ fontSize:12, color:C.green, fontWeight:600, marginBottom:8 }}>رقم السنة: {pyear}</div>
            <div style={{ fontSize:14, lineHeight:1.75 }}>{pyrd.title} — {pyrd.desc}</div>
            {pyear===rd.contra && (
              <div style={{ fontSize:12, color:C.text2, lineHeight:1.6, borderTop:`1px solid ${C.border}`, paddingTop:10, marginTop:10 }}>
                <ul style={{ listStyle:'none', padding:0, margin:0 }}>
                  <li style={{ paddingRight:14, position:'relative', lineHeight:1.6 }}><span style={{ position:'absolute', right:0, color:C.goldDim, fontSize:7, top:4 }}>◆</span>⭐ توافق نادر: سنتك الشخصية 2026 هي نفس رقمك المضاد! هذه السنة هي الأمثل للتحول.</li>
                </ul>
              </div>
            )}
          </RCard>
        </Card>
      )}

      {/* Love / Money / Work */}
      {[
        { key:'love', label:'❤️ في الحب', color:C.purple },
        { key:'money', label:'💰 في المال', color:C.green },
        { key:'work', label:'💼 في العمل', color:C.accent },
      ].map(item => (
        <div key={item.key} style={{ background:C.bg2, borderRadius:14, padding:'16px 18px', marginBottom:12, border:`1px solid ${C.border}`, borderRight:`3px solid ${item.color}` }}>
          <div style={{ fontSize:12, color:item.color, fontWeight:600, marginBottom:8 }}>{item.label}</div>
          <div style={{ fontSize:14, lineHeight:1.75 }}>{rd[item.key]}</div>
        </div>
      ))}

      {/* Activation */}
      {rd.activation && (
        <Card>
          <CardTitle>تفعيل الطاقة اليومي</CardTitle>
          <div style={{ fontSize:13, color:C.text2, lineHeight:1.8 }}>{rd.activation}</div>
        </Card>
      )}

      {/* Subscription */}
      <Card>
        <CardTitle>اشتراكك</CardTitle>
        {subscription ? (
          <div>
            {[['الخطة',subscription.name_ar||subscription.plan_type],['أسئلة/يوم',subscription.asks_per_day],['ينتهي',subscription.end_date||'—']].map(([l,v])=>(
              <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:`1px solid ${C.border}` }}>
                <span style={{ fontSize:13, color:C.text }}>{v}</span>
                <span style={{ fontSize:13, color:C.text2 }}>{l}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign:'center', color:C.text2, padding:'16px 0' }}>
            <div>لا يوجد اشتراك فعال</div>
            <div style={{ color:C.red, fontSize:12, marginTop:4 }}>تواصل مع المسؤول للتجديد</div>
          </div>
        )}
      </Card>

      <button type="button" onClick={onLogout}
        style={{ background:'none', border:`1px solid ${C.red}`, borderRadius:10, padding:'9px 18px', color:'#E07070', fontSize:12, cursor:'pointer', display:'block', margin:'14px auto', fontFamily:'Cairo,sans-serif' }}>
        تغيير المستخدم
      </button>
    </div>
  );
}
